import { Banknote, TrendingUp, AlertTriangle, Percent } from "lucide-react";
import api, { zar } from "@/lib/api";
import { PageHeader, useFetch, Spinner, StatCard } from "@/components/portal/ui";
import { StatusBadge } from "@/components/StatusBadge";

function fmt(t) { return t ? new Date(t).toLocaleDateString("en-ZA", { dateStyle: "medium" }) : "—"; }

export default function Finance() {
  const { data, loading } = useFetch(() => api.get("/finance").then((r) => r.data));
  if (loading) return <Spinner />;
  const s = data.summary;

  return (
    <div>
      <PageHeader title="Finance" subtitle="ERP-ready · Configurable tax engine" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="fin-revenue" label="Revenue (Paid)" value={zar(s.revenue_paid)} icon={Banknote} accent="text-emerald-600" />
        <StatCard testid="fin-ar" label="Accounts Receivable" value={zar(s.accounts_receivable)} icon={TrendingUp} accent="text-blue-600" />
        <StatCard testid="fin-overdue" label="Overdue" value={zar(s.overdue)} icon={AlertTriangle} accent="text-red-600" />
        <StatCard testid="fin-collection" label="Collection Rate" value={`${s.collection_rate}%`} icon={Percent} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card overflow-x-auto">
          <div className="p-4 border-b border-border"><h2 className="font-heading font-bold">Invoices (AR / AP)</h2></div>
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="p-4">Invoice</th><th className="p-4 hidden md:table-cell">Customer</th><th className="p-4">Total</th><th className="p-4 hidden lg:table-cell">Due</th><th className="p-4">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.invoices.slice(0, 15).map((i) => (
                <tr key={i.id} className="hover:bg-muted/40">
                  <td className="p-4 font-mono font-semibold">{i.invoice_number}</td>
                  <td className="p-4 hidden md:table-cell text-muted-foreground">{i.customer_name}</td>
                  <td className="p-4 font-semibold">{zar(i.total_zar)}</td>
                  <td className="p-4 hidden lg:table-cell text-muted-foreground">{fmt(i.due_date)}</td>
                  <td className="p-4"><StatusBadge status={i.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-xl border border-border bg-card p-5 h-fit">
          <h2 className="font-heading font-bold flex items-center gap-2"><Percent size={18} className="text-primary" /> Configurable Tax Engine</h2>
          <p className="text-xs text-muted-foreground mt-1">Rates are configurable — not hard-coded.</p>
          <div className="mt-4 space-y-3">
            {data.tax_config?.rates?.map((t) => (
              <div key={t.name} className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
                <div>
                  <p className="text-sm font-semibold">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.region}</p>
                </div>
                <span className="font-heading font-extrabold text-primary">{t.rate_pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
